import { useEffect, useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { FamilyEditorSchemaDto } from './types'
import { DecimalInput } from '@/components/ui/DecimalInput'
import { parseDecimalInput } from '@/lib/decimal'

interface FamilyEditorPricingSectionProps {
  schema: FamilyEditorSchemaDto
  quantity: number | null
  onQuantityChange: (next: number | null) => void
  unitPrice: number | null
  onUnitPriceChange: (next: number | null) => void
  vatPercent: number | null
  onVatPercentChange: (next: number | null) => void
  priceIncluded: boolean
  onPriceIncludedChange: (next: boolean) => void
}

export function FamilyEditorPricingSection({
  schema,
  quantity,
  onQuantityChange,
  unitPrice,
  onUnitPriceChange,
  vatPercent,
  onVatPercentChange,
  priceIncluded,
  onPriceIncludedChange,
}: FamilyEditorPricingSectionProps) {
  const [quantityRaw, setQuantityRaw] = useState('')
  const [unitPriceRaw, setUnitPriceRaw] = useState('')

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuantityRaw(typeof quantity === 'number' && Number.isFinite(quantity) ? String(quantity) : '')
  }, [quantity])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUnitPriceRaw(typeof unitPrice === 'number' && Number.isFinite(unitPrice) ? String(unitPrice) : '')
  }, [unitPrice])

  return (
    <div className="border-t pt-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Qté</Label>
          <DecimalInput
            data-testid="editor-qty-input"
            className="h-9"
            value={quantityRaw}
            onValueChange={setQuantityRaw}
            allowNegative={false}
            onBlur={() => {
              const parsed = parseDecimalInput(quantityRaw, { allowNegative: false })
              if (parsed == null) {
                onQuantityChange(quantityRaw.trim() === '' ? null : null)
                return
              }
              onQuantityChange(Math.trunc(parsed))
            }}
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Prix unitaire HT</Label>
          <div className="relative">
            <DecimalInput
              data-testid="editor-price-input"
              className="h-9 pr-8"
              value={unitPriceRaw}
              onValueChange={setUnitPriceRaw}
              allowNegative={false}
              onBlur={() => {
                const parsed = parseDecimalInput(unitPriceRaw, { allowNegative: false })
                onUnitPriceChange(parsed)
              }}
            />
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              €
            </div>
          </div>
        </div>

        {schema.pricing?.vatRateEditable === true ? (
          <div className="space-y-1">
            <Label className="text-xs">TVA</Label>
            <Select
              value={vatPercent != null ? String(vatPercent) : undefined}
              onValueChange={(v) => onVatPercentChange(parseInt(v, 10))}
            >
              <SelectTrigger data-testid="editor-vat-select" className="h-9 w-full">
                <SelectValue placeholder="TVA" />
              </SelectTrigger>
              <SelectContent>
                {(schema.pricing?.availableVats || []).map((r) => (
                  <SelectItem key={String(r.value)} value={String(r.value)}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div />
        )}
      </div>

      {schema.pricing?.canPriceBeIncluded === true ? (
        <div className="mt-2 flex items-center gap-2">
          <Checkbox
            id="fes-pricing-priceIncluded"
            checked={priceIncluded}
            onCheckedChange={(v) => onPriceIncludedChange(v === true)}
          />
          <Label htmlFor="fes-pricing-priceIncluded" className="text-sm">
            Inclus
          </Label>
        </div>
      ) : null}
    </div>
  )
}
